import { useCallback, useEffect, useMemo, useState } from "react";
import type { DashboardChartsResponse, DashboardStat } from "utils/dashboardApi";
import {
  fetchDashboardCharts,
  fetchDashboardStats,
  DashboardChartsParams,
} from "utils/dashboardApi";

type DashboardOverviewData = {
  stats: DashboardStat[];
  charts: DashboardChartsResponse["charts"];
  meta?: DashboardChartsResponse["meta"];
  statsGeneratedAt?: string;
  chartsGeneratedAt?: string;
};

type UseDashboardOverviewOptions = DashboardChartsParams;

const STATS_POLL_INTERVAL_MS = 30_000;

export function useDashboardOverview(options: UseDashboardOverviewOptions) {
  const chartOptions = useMemo(
    () => ({
      date: options.date,
      range: options.range,
      groupBy: options.groupBy,
    }),
    [options.date, options.range, options.groupBy]
  );

  const statsOptions = useMemo(
    () => ({
      date: options.date,
    }),
    [options.date]
  );

  const [data, setData] = useState<DashboardOverviewData | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [statsError, setStatsError] = useState<Error | null>(null);
  const [chartsError, setChartsError] = useState<Error | null>(null);
  const [statsRefreshIndex, setStatsRefreshIndex] = useState(0);
  const [chartsRefreshIndex, setChartsRefreshIndex] = useState(0);

  const refetch = useCallback(() => {
    setStatsRefreshIndex((prev) => prev + 1);
    setChartsRefreshIndex((prev) => prev + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;
    let isFirstRequest = true;
    const controllers = new Set<AbortController>();
    setStatsLoading(true);
    setStatsError(null);

    const finishFirstRequest = () => {
      if (isMounted && isFirstRequest) {
        setStatsLoading(false);
        isFirstRequest = false;
      }
    };

    const loadStats = async () => {
      const controller = new AbortController();
      controllers.add(controller);

      try {
        const payload = await fetchDashboardStats(statsOptions, controller.signal);
        if (!isMounted) {
          return;
        }
        setData((previous) => ({
          stats: payload.stats,
          charts: previous?.charts ?? {},
          meta: previous?.meta,
          statsGeneratedAt: payload.generatedAt,
          chartsGeneratedAt: previous?.chartsGeneratedAt,
        }));
        setStatsError(null);
      } catch (err) {
        if (!isMounted || controller.signal.aborted) {
          return;
        }
        setStatsError(err as Error);
      } finally {
        controllers.delete(controller);
        finishFirstRequest();
      }
    };

    loadStats();
    const intervalId = window.setInterval(loadStats, STATS_POLL_INTERVAL_MS);

    return () => {
      isMounted = false;
      controllers.forEach((controller) => controller.abort());
      window.clearInterval(intervalId);
    };
  }, [statsOptions, statsRefreshIndex]);

  useEffect(() => {
    const controller = new AbortController();
    setChartsLoading(true);
    setChartsError(null);

    fetchDashboardCharts(chartOptions, controller.signal)
      .then((payload) => {
        setData((previous) => ({
          stats: previous?.stats ?? [],
          charts: payload.charts,
          meta: payload.meta,
          statsGeneratedAt: previous?.statsGeneratedAt,
          chartsGeneratedAt: payload.meta.generatedAt,
        }));
      })
      .catch((err) => {
        if (!controller.signal.aborted) {
          setChartsError(err as Error);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setChartsLoading(false);
        }
      });

    return () => controller.abort();
  }, [chartOptions, chartsRefreshIndex]);

  return {
    data,
    loading: statsLoading || chartsLoading,
    statsLoading,
    chartsLoading,
    error: statsError ?? chartsError,
    statsError,
    chartsError,
    refetch,
  };
}
